import React, { useState, useCallback, useMemo } from 'react';
import { View, ScrollView, TouchableOpacity, TextInput, ActivityIndicator, KeyboardAvoidingView, Platform, Image as RNImage, Linking } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useFocusEffect } from '@react-navigation/native';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import { Icon as SvgIcon } from '../../../../components/Icon';
import api from '../../../../utils/api';
import { useSelector } from 'react-redux';
import useActiveResidentCommunity from '../../../../hooks/useActiveResidentCommunity';


const STATUS_STYLES: any = {
  nuevo: { bg: 'bg-red-50', text: 'text-red-600', label: 'Nuevo' },
  en_seguimiento: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'En seguimiento' },
  atendida: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Atendida' },
};


export default function TicketDetailScreen({ route, navigation }: any) {
  const { ticketUuid } = route.params;
  const { communityId } = useActiveResidentCommunity();
  const { user } = useSelector((s: any) => s.auth);

  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const ticketQuery = useQuery<any>({
    queryKey: ['resident.ticket', communityId, ticketUuid],
    queryFn: () => api.get(`/communities/${communityId}/tickets/${ticketUuid}`).then(r => r.data),
    enabled: !!communityId && !!ticketUuid,
  });

  useFocusEffect(useCallback(() => {
    ticketQuery.refetch();
  }, [communityId, ticketUuid]));

  const ticket = ticketQuery.data;

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/communities/${communityId}/tickets/${ticketUuid}/comments`, {
        body: newComment.trim(),
      });
      setNewComment('');
      ticketQuery.refetch();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al enviar');
    } finally {
      setPosting(false);
    }
  };

  const publicComments: any[] = useMemo(() => {
    return ticket?.public_comments ?? ticket?.publicComments ?? [];
  }, [ticket?.public_comments, ticket?.publicComments]);

  if (!ticket) {
    return (
      <View style={tw`flex-1 items-center justify-center`}>
        <ActivityIndicator color="#6366f1" />
      </View>
    );
  }

  const status = STATUS_STYLES[ticket.status] ?? STATUS_STYLES.nuevo;

  return (
    <KeyboardAvoidingView
      behavior="padding"
      style={tw`flex-1 bg-background`}
    >
      <View style={tw`bg-secondary pt-14 pb-5 px-5 flex-row items-center`}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={tw`mr-3`}>
          <Icon name="chevron-left" color="white" size={20} />
        </TouchableOpacity>
        <View style={tw`flex-1`}>
          <Text style={tw`text-white/70 text-xs`}>{ticket.folio}</Text>
          <View style={tw`flex-row items-center mt-1 gap-2`}>
            <Text style={tw.style(`text-xs font-500 px-2 py-0.5 rounded-full`, status.bg, status.text)}>
              {status.label}
            </Text>
            {ticket.is_high_priority && (
              <Text style={tw`text-red-300 text-sm font-700`}>!!</Text>
            )}
            <Text style={tw`text-xs text-white/80`}>
              {ticket.area?.name ?? 'Por asignar'}
            </Text>
          </View>
        </View>
      </View>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`p-4 pb-6`}>
        {/* Mensaje inicial: descripción del reporter */}
        {ticket.description ? (
          <InitialMessage
            authorName={ticket.reporter?.name}
            at={ticket.created_at}
            body={ticket.description}
            mine={true}
          />
        ) : null}

        {/* Mensaje inicial: imágenes (si existen) */}
        {ticket.files?.length > 0 ? (
          <InitialImages
            authorName={ticket.reporter?.name}
            at={ticket.created_at}
            files={ticket.files}
            mine={true}
            hideHeader={!!ticket.description}
          />
        ) : null}

        {/* Comentarios posteriores */}
        {publicComments.map((c: any) => {
          // El solicitante (reporter) va a la derecha en verde; el admin a la izquierda en morado.
          const isReporter = !!c.user?.uuid && c.user.uuid === ticket.reporter?.uuid;
          return (
            <View
              key={c.uuid}
              style={tw.style(`mb-2`, isReporter ? 'items-end' : 'items-start')}
            >
              <Text style={tw.style(`text-xs text-gray-500 mb-1 px-1`, isReporter ? 'self-end' : 'self-start')}>
                {c.user?.name} · {dayjs(c.created_at).format('D MMM H:mm')}
              </Text>
              <View style={tw.style(
                `rounded-xl border px-3 py-2 max-w-[80%]`,
                isReporter ? 'bg-primary-50 border-primary/40' : 'bg-secondary-50 border-secondary/40',
              )}>
                <Text style={tw.style(`text-sm text-gray-700`, isReporter && `text-right`)}>{c.body}</Text>
              </View>
            </View>
          );
        })}

        {!ticket.description && ticket.files?.length === 0 && publicComments.length === 0 && (
          <Text style={tw`text-xs text-gray-400 text-center mt-10`}>Sin actividad aún</Text>
        )}
      </ScrollView>

      <View style={tw`bg-white border-t border-gray-200 px-4 py-3 flex-row items-end gap-2`}>
        <TextInput
          value={newComment}
          onChangeText={setNewComment}
          placeholder="Escribe un mensaje…"
          placeholderTextColor={tw.color('gray-400')}
          multiline
          style={tw`flex-1 bg-gray-100 border border-gray-300 rounded-2xl px-4 py-2 text-sm max-h-24`}
        />
        <TouchableOpacity
          onPress={submitComment}
          disabled={posting || !newComment.trim()}
          style={tw.style(`w-10 h-10 rounded-full items-center justify-center`, !newComment.trim() ? 'bg-gray-200' : 'bg-primary')}
        >
          <SvgIcon name="send" color={!newComment.trim() ? tw.color('gray-400') : tw.color('white')} size={16} />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}


function InitialMessage({ authorName, at, body, mine }: any) {
  return (
    <View style={tw.style(`mb-2`, mine ? 'items-end' : 'items-start')}>
      <Text style={tw.style(`text-xs text-gray-500 mb-1 px-1`, mine ? 'self-end' : 'self-start')}>
        {authorName} · {dayjs(at).format('D MMM H:mm')}
      </Text>
      <View style={tw.style(`rounded-xl border px-3 py-2 max-w-[85%]`, mine ? 'bg-primary-50 border-primary/40' : 'bg-secondary-50 border-secondary/40')}>
        <Text style={tw.style(`text-sm text-gray-700`, mine && `text-right`)}>{body}</Text>
      </View>
    </View>
  );
}

function InitialImages({ authorName, at, files, mine, hideHeader }: any) {
  return (
    <View style={tw.style(`mb-3`, mine ? 'items-end' : 'items-start')}>
      {!hideHeader && (
        <Text style={tw.style(`text-xs text-gray-500 mb-1 px-1`, mine ? 'self-end' : 'self-start')}>
          {authorName} · {dayjs(at).format('D MMM H:mm')}
        </Text>
      )}
      <View style={tw`flex-row flex-wrap gap-2 max-w-[85%]`}>
        {files.map((f: any) => (
          <TouchableOpacity key={f.uuid} onPress={() => Linking.openURL(f.url)}>
            <RNImage source={{ uri: f.url }} style={tw`w-24 h-24 rounded-lg bg-gray-200`} />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}
